/**
 * Legal Page Layout Component
 * Clean, minimal design inspired by X.com privacy page
 * Uses on record Color Bible
 */

import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface LegalPageLayoutProps {
  title: string
  lastUpdated?: string
  children: React.ReactNode
}

export default function LegalPageLayout({ title, lastUpdated, children }: LegalPageLayoutProps) {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header */}
      <header className="sticky top-0 bg-white border-b border-gray-100 z-10">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo/Brand */}
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: '#111111' }} />
              </button>
              <span className="text-xl font-bold" style={{ color: '#0A2342' }}>
                OnRecord
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
        {/* Title */}
        <div className="mb-12">
          <h1
            className="text-4xl sm:text-5xl font-bold mb-3"
            style={{ color: '#111111' }}
          >
            {title}
          </h1>
          {lastUpdated && (
            <p className="text-base" style={{ color: '#6B7280' }}>
              Effective: {lastUpdated}
            </p>
          )}
        </div>

        {/* Legal Content */}
        <div className="prose prose-lg max-w-none">
          {children}
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="border-t border-gray-100 mt-20">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8">
          <div className="flex flex-wrap gap-6 text-sm" style={{ color: '#6B7280' }}>
            <a href="/about" target="_blank" rel="noopener noreferrer" className="hover:underline">
              About
            </a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:underline">
              Terms
            </a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">
              Privacy
            </a>
            <a href="/cookies" target="_blank" rel="noopener noreferrer" className="hover:underline">
              Cookies
            </a>
          </div>
          <p className="mt-4 text-sm" style={{ color: '#6B7280' }}>
            © {new Date().getFullYear()} OnRecord. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
